import React from 'react'
import { ButtonGroup } from '@pandiafi/design-system'

import {
  Contract,
  ContractTenant,
  FeatureFlag,
} from '../../../../../common/validations'
import { getValue } from './utils/getValue'
import { useSuggestions } from './utils/useSuggestions'
import { isTerminationDisallowedBecauseOfPending } from '../../../ContractChanges/utils/isTerminationDisallowedBecauseOfPending'
import { getTenantName } from '../../../../utils/getTenantName'
import { resolveTenantStatusLabel } from '../../../ContractChanges/components/TenantChoice/resolveTenantStatusLabel'
import { LockedTenantChoice } from '../../../ContractChanges/components/LockedTenantChoice/LockedTenantChoice'

import FormFieldWrapper from 'components/FormField'
import { TerminateSuggestionAction } from 'views/ContractChanges/hooks/useTerminateSuggestions'

interface TenantSelectorProps {
  tenants: ContractTenant[]
  contract: Contract
  terminate: number[]
  handleTerminateChange: (state: number[]) => void
  featureFlags?: FeatureFlag
}

export const TenantSelector: React.FC<TenantSelectorProps> = ({
  tenants,
  contract,
  terminate,
  handleTerminateChange,
}) => {
  const { getSuggestions } = useSuggestions(contract)
  const showPendingState = isTerminationDisallowedBecauseOfPending(contract)
  const disableTenantSelection =
    Boolean(contract.terminationDisallowedReason) && !showPendingState
  const tenantSelectorClassName = 'mt-2 phone:w-60 w-80'

  return tenants.map((tenant) => {
    const suggestions = getSuggestions(tenant, terminate)
    const value = getValue(contract, tenant, terminate.includes(tenant.id))
    const tenantName = getTenantName(tenant)

    const handleOnChange = (suggestionId: string | undefined) => {
      if (suggestionId === TerminateSuggestionAction.TERMINATE) {
        handleTerminateChange(tenants.map((x) => x.id))
      } else {
        handleTerminateChange([])
      }
    }

    const statusLabel = resolveTenantStatusLabel(
      contract,
      tenant,
      showPendingState
    )

    return (
      <FormFieldWrapper
        key={tenant.id}
        name="tenant"
        label={tenantName}
        labelLocation="left"
        className="mb-4"
      >
        <div className={tenantSelectorClassName}>
          {statusLabel ? (
            <LockedTenantChoice label={statusLabel} tenantName={tenantName} />
          ) : (
            <ButtonGroup
              className="w-full"
              onChange={handleOnChange}
              suggestions={suggestions}
              value={value}
              disabled={disableTenantSelection}
              sameSize
              data-testid="tenantSelector"
            />
          )}
        </div>
      </FormFieldWrapper>
    )
  })
}
